/**
 * The cloud's offline-auth routes (D20; `offlineAuth.ts`), spread into
 * `ROUTES` by `routes.ts`: a user's own offline PIN, a site's emergency
 * account, and the audit of break-glass use the nodes report.
 *
 * Cloud only: a node serves its own sign-in routes (`/local/*`, outside the
 * table). Typed with `satisfies` against a type-only import, as
 * `tenancyRoutes.ts`.
 */
import {
  emergencyAccountRequestWireSchema,
  emergencyAccountResponseWireSchema,
  emergencyAccountStatusWireSchema,
  listAuditQueryWireSchema,
  listAuditResponseWireSchema,
  offlinePinResponseWireSchema,
  setOfflinePinRequestWireSchema,
} from './offlineAuth.js';
import type { RouteDef } from './routes.js';
import { sitePathWireSchema } from './sites.js';

export const OFFLINE_AUTH_ROUTES = {
  getOfflinePin: {
    id: 'getOfflinePin',
    method: 'GET',
    path: '/me/offline-pin',
    tag: 'tenancy',
    summary: 'Whether you have an offline PIN',
    description: 'Whether the caller has an offline PIN set, and when it was set. The PIN itself is never returned — the cloud holds only its Argon2id hash.',
    params: null,
    query: null,
    body: null,
    response: offlinePinResponseWireSchema,
    successStatus: 200,
    permission: null,
    permissionScope: 'tenant',
    errors: [],
  },
  setOfflinePin: {
    id: 'setOfflinePin',
    method: 'PUT',
    path: '/me/offline-pin',
    tag: 'tenancy',
    summary: 'Set your offline PIN',
    description:
      'Sets (or replaces) the caller’s break-glass PIN (D20): stored as an Argon2id hash and synced to the nodes of every site the caller’s groups reach, where it signs the caller in ONLY while that node cannot reach the cloud. Requires `local.login` somewhere in the tenant; a node also checks it for its own site at sign-in.',
    params: null,
    query: null,
    body: setOfflinePinRequestWireSchema,
    response: offlinePinResponseWireSchema,
    successStatus: 200,
    permission: 'local.login',
    permissionScope: 'resource',
    errors: ['bad-request', 'validation-failed', 'payload-too-large'],
  },
  clearOfflinePin: {
    id: 'clearOfflinePin',
    method: 'DELETE',
    path: '/me/offline-pin',
    tag: 'tenancy',
    summary: 'Remove your offline PIN',
    description: 'Removes the caller’s offline PIN; nodes forget it at the next sync. Idempotent.',
    params: null,
    query: null,
    body: null,
    response: offlinePinResponseWireSchema,
    successStatus: 200,
    permission: null,
    permissionScope: 'tenant',
    errors: [],
  },
  createEmergencyAccount: {
    id: 'createEmergencyAccount',
    method: 'POST',
    path: '/sites/{siteId}/emergency-account',
    tag: 'sites',
    summary: 'Create or rotate a site’s emergency account',
    description:
      'Creates the site’s emergency account (D20) — `emergency` with a generated password, shown in this response ONCE; the cloud keeps only its Argon2id hash, synced to the site’s nodes. It signs in on those nodes only while they cannot reach the cloud, with operator rights on that site. `rotate: true` replaces an existing password (its sessions end at the next sync); `rotate: false` creates it, and an existing account is `conflict`. On-prem sites only.',
    params: sitePathWireSchema,
    query: null,
    body: emergencyAccountRequestWireSchema,
    response: emergencyAccountResponseWireSchema,
    successStatus: 201,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'validation-failed', 'not-found', 'conflict', 'payload-too-large'],
  },
  getEmergencyAccount: {
    id: 'getEmergencyAccount',
    method: 'GET',
    path: '/sites/{siteId}/emergency-account',
    tag: 'sites',
    summary: 'Whether a site has an emergency account',
    description: 'Whether the site has an emergency account (D20) and when its password was last set — never the password, which only the create or rotate answer carries.',
    params: sitePathWireSchema,
    query: null,
    body: null,
    response: emergencyAccountStatusWireSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request', 'not-found'],
  },
  listAudit: {
    id: 'listAudit',
    method: 'GET',
    path: '/audit',
    tag: 'tenancy',
    summary: 'The tenant’s break-glass audit',
    description:
      'Every offline sign-in attempt (PIN or emergency account) the tenant’s nodes recorded while cut off from the cloud and reported once reconnected (D20), newest first. Kept one year.',
    params: null,
    query: listAuditQueryWireSchema,
    body: null,
    response: listAuditResponseWireSchema,
    successStatus: 200,
    permission: 'tenant.admin',
    permissionScope: 'tenant',
    errors: ['bad-request'],
  },
  listVendorAudit: {
    id: 'listVendorAudit',
    method: 'GET',
    path: '/vendor/audit',
    tag: 'vendor',
    summary: 'Break-glass audit across tenants',
    description: 'The same entries as `GET /audit`, for every tenant of this regional deployment, newest first (D27). Vendor tenant only.',
    params: null,
    query: listAuditQueryWireSchema,
    body: null,
    response: listAuditResponseWireSchema,
    successStatus: 200,
    permission: 'vendor.ops',
    permissionScope: 'tenant',
    errors: ['bad-request'],
  },
} as const satisfies Record<string, RouteDef>;
